import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useQuery } from '../api.js';
import { Drawer, ErrorNote, Pane, Panes, Pill, Spinner, useToast } from '../ui.js';
import { History } from './History.js';

interface Squawk {
  id: number; aircraftId: number; tail: string; reportedOn: string; reportedByName: string;
  title: string; body: string; status: string; restriction: string;
  affected: { id: number; when: string; who: string; instructorName: string }[];
  affectedCount: number;
}
interface Inspection {
  id: number; aircraftId: number; tail: string; kind: string;
  dueHobbs: number | null; dueOn: string | null; note: string;
  remainingHours: number | null; grounded: boolean;
}
interface Data {
  squawks: Squawk[];
  inspections: Inspection[];
  aircraft: { id: number; tail: string; model: string }[];
  canFile: boolean;
  canResolve: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  grounded: 'Grounded', 'day-vfr': 'Day VFR only', open: 'Open',
  monitoring: 'Monitoring', deferred: 'Deferred', closed: 'Closed',
};

/**
 * Airworthiness: what is squawked and what is due. Rendered under the fleet
 * rather than on a tab of its own, because it is about the same aircraft.
 */
export function MaintenanceSections(): JSX.Element {
  const { data, loading, error, reload } = useQuery<Data>('/maintenance');
  const [filing, setFiling] = useState(false);
  const [openSquawk, setOpenSquawk] = useState<Squawk | null>(null);

  if (error) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  return (
    <div className="split split--even" id="airworthiness" style={{ padding: 0 }}>
      <div className="split__main card">
        <div className="card__head">
          <span className="card__title">Open squawks · {data.squawks.length}</span>
          {data.canFile && <button className="btn btn--primary" onClick={() => setFiling(true)}>File a squawk</button>}
        </div>
        <div className="split__scroll">
        {data.squawks.length === 0 && <div className="empty">Nothing open. The whole fleet is signed off.</div>}
        {data.squawks.map((s) => (
          <div key={s.id} id={`squawk-${s.id}`} className="squawk">
            <span className={`finding__dot ${dotFor(s.status)}`} />
            <div className="squawk__tail">
              <div style={{ fontWeight: 700, fontSize: 12.5 }}>{s.tail}</div>
              <div style={{ fontSize: 11, color: 'var(--muted-2)' }}>{s.reportedOn}</div>
            </div>
            <div className="squawk__body">
              <div style={{ fontWeight: 700, fontSize: 12.5 }}>{s.title}</div>
              <div className="small muted" style={{ textWrap: 'pretty' }}>{s.body}</div>
              <div style={{ fontSize: 11, color: 'var(--muted-3)', marginTop: 2 }}>Reported by {s.reportedByName}</div>
            </div>
            <div className="squawk__state">
              <Pill>{STATUS_LABEL[s.status] ?? s.status}</Pill>
              <div style={{ fontSize: 11, color: s.affectedCount ? 'var(--conflict-c)' : 'var(--muted-2)', marginTop: 3 }}>
                {s.affectedCount
                  ? `${s.affectedCount} reservation${s.affectedCount === 1 ? '' : 's'} affected`
                  : 'No reservations affected'}
              </div>
              <button className="btn btn--link" onClick={() => setOpenSquawk(s)}>Open</button>
            </div>
          </div>
        ))}
        </div>
      </div>

      <aside className="split__aside card">
        <div className="card__head"><span className="card__title">Inspections due · {data.inspections.length}</span></div>
        <div className="split__scroll">
        {data.inspections.length === 0 && <div className="empty">Nothing coming due.</div>}
        {data.inspections.map((i) => (
          <div key={i.id} style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-4)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontWeight: 700, fontSize: 12.5 }}>{i.tail}</span>
              <span style={{
                fontSize: 11.5, fontWeight: 700,
                color: i.grounded || (i.remainingHours != null && i.remainingHours < 15) ? 'var(--conflict-c)' : 'var(--text-2)',
              }}>
                {i.grounded ? 'In work' : i.remainingHours != null ? `${i.remainingHours.toFixed(1)} hrs` : i.dueOn}
              </span>
            </div>
            <div className="small muted">{i.kind}</div>
            <div style={{ fontSize: 11, color: 'var(--muted-3)' }}>{i.note}</div>
          </div>
        ))}
        </div>
        {loading && <div className="small muted" style={{ padding: 8 }}>Refreshing…</div>}
      </aside>

      {filing && <FileSquawk aircraft={data.aircraft} onClose={() => setFiling(false)} onFiled={() => { setFiling(false); reload(); }} />}
      {openSquawk && (
        <SquawkDrawer
          squawk={openSquawk}
          canResolve={data.canResolve}
          onClose={() => setOpenSquawk(null)}
          onChanged={() => { setOpenSquawk(null); reload(); }}
        />
      )}
    </div>
  );
}

function dotFor(status: string): string {
  if (status === 'grounded') return 'sev-conflict';
  if (status === 'day-vfr' || status === 'open') return 'sev-review';
  return 'sev-info';
}

function FileSquawk({ aircraft, onClose, onFiled }: {
  aircraft: { id: number; tail: string; model: string }[];
  onClose: () => void; onFiled: () => void;
}): JSX.Element {
  const toast = useToast();
  const [aircraftId, setAircraftId] = useState(aircraft[0]?.id ?? 0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState('open');
  const [restriction, setRestriction] = useState('none');
  const [busy, setBusy] = useState(false);
  const [affected, setAffected] = useState<{ id: number; studentName: string; notes: string; startsAt: number }[] | null>(null);

  const submit = async () => {
    setBusy(true);
    try {
      const result = await api.post<{ affected: { id: number; studentName: string; notes: string; startsAt: number }[] }>(
        '/maintenance/squawks',
        { aircraftId, title, body, status, restriction: status === 'grounded' ? 'all' : restriction },
      );
      if (result.affected.length) {
        setAffected(result.affected);
        toast.error(
          `${result.affected.length} reservation${result.affected.length === 1 ? '' : 's'} affected`,
          'Move or cancel them before the aircraft is due out.',
        );
      } else {
        toast.ok('Squawk filed', 'No reservations were affected.');
        onFiled();
      }
    } catch (err) {
      toast.error('Could not file that', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const navigate = useNavigate();

  return (
    <Drawer
      narrow
      title="File a squawk"
      subtitle="Grounding an aircraft surfaces every reservation it touches."
      onClose={affected ? onFiled : onClose}
      footer={affected
        ? <button className="btn btn--primary" onClick={onFiled}>Done</button>
        : <button className="btn btn--primary" disabled={busy || title.trim().length < 3} onClick={submit}>File squawk</button>}
    >
      {affected ? (
        <>
          <div className="pane__head">These reservations need to move</div>
          {affected.map((r) => (
            <div className="kv" key={r.id}>
              <span style={{ fontWeight: 400 }}>
                {new Date(r.startsAt).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                {' '}{new Date(r.startsAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
              </span>
              <button className="btn btn--link" onClick={() => navigate(`/schedule?reservation=${r.id}`)}>
                {r.studentName || r.notes || 'Open'}
              </button>
            </div>
          ))}
        </>
      ) : (
        <div className="card__body stack">
          <label className="field">
            <span>Aircraft</span>
            <select className="select" value={aircraftId} onChange={(e) => setAircraftId(Number(e.target.value))}>
              {aircraft.map((a) => <option key={a.id} value={a.id}>{a.tail} · {a.model}</option>)}
            </select>
          </label>
          <label className="field">
            <span>What was found</span>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Right magneto drop 210 rpm" />
          </label>
          <label className="field">
            <span>Detail</span>
            <textarea className="textarea" value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Where it was found, what was done, what happens next." />
          </label>
          <label className="field">
            <span>Disposition</span>
            <select className="select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="grounded">Grounded · aircraft is unairworthy</option>
              <option value="day-vfr">Day VFR only</option>
              <option value="open">Open · no dispatch restriction</option>
              <option value="monitoring">Monitoring</option>
              <option value="deferred">Deferred to next maintenance</option>
            </select>
          </label>
          {status !== 'grounded' && (
            <label className="field">
              <span>Dispatch restriction</span>
              <select className="select" value={restriction} onChange={(e) => setRestriction(e.target.value)}>
                <option value="none">None</option>
                <option value="night">No night flight</option>
                <option value="ifr">No instrument flight</option>
              </select>
            </label>
          )}
          <span className="small muted">
            A grounding squawk marks the aircraft unairworthy under 14 CFR 91.7(a) and every future
            reservation on it becomes a conflict until the squawk is closed.
          </span>
        </div>
      )}
    </Drawer>
  );
}

function SquawkDrawer({ squawk, canResolve, onClose, onChanged }: {
  squawk: Squawk; canResolve: boolean; onClose: () => void; onChanged: () => void;
}): JSX.Element {
  const toast = useToast();
  const navigate = useNavigate();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const setStatus = async (status: string) => {
    setBusy(true);
    try {
      const result = await api.post<{ aircraftStatus: string }>(`/maintenance/squawks/${squawk.id}/status`, { status, note });
      toast.ok(
        status === 'closed' ? 'Squawk closed' : 'Squawk updated',
        `${squawk.tail} is now ${result.aircraftStatus}.`,
      );
      onChanged();
    } catch (err) {
      toast.error('Could not update', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      title={`${squawk.tail} · ${squawk.title}`}
      subtitle={`Reported ${squawk.reportedOn} by ${squawk.reportedByName}`}
      onClose={onClose}
      footer={canResolve && (
        <>
          <button className="btn btn--primary" disabled={busy} onClick={() => setStatus('closed')}>Sign off and close</button>
          <button className="btn" disabled={busy} onClick={() => setStatus('monitoring')}>Monitor</button>
          <button className="btn" disabled={busy} onClick={() => setStatus('deferred')}>Defer</button>
        </>
      )}
    >
      <Panes>
        <Pane title="The squawk">
          <div className="row" style={{ gap: 6, marginBottom: 12 }}>
            <Pill>{STATUS_LABEL[squawk.status] ?? squawk.status}</Pill>
          </div>
          <p style={{ margin: 0, textWrap: 'pretty' }}>
            {squawk.body || <span className="muted">No further detail recorded.</span>}
          </p>
          {canResolve && (
            <label className="field" style={{ marginTop: 16 }}>
              <span>Maintenance note (appended to the squawk)</span>
              <textarea className="textarea" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Sender replaced, ground run satisfactory." />
            </label>
          )}
        </Pane>

        <Pane title={`Affected reservations · ${squawk.affectedCount}`} flush>
          {squawk.affected.length === 0 && (
            <div className="empty" style={{ padding: 20 }}>Nothing on the schedule is affected.</div>
          )}
          {squawk.affected.map((r) => (
            <div className="kv" key={r.id}>
              <span style={{ fontWeight: 400, textAlign: 'left' }}>{r.when} · {r.instructorName || 'no instructor'}</span>
              <button className="btn btn--link" onClick={() => navigate(`/schedule?reservation=${r.id}`)}>{r.who}</button>
            </div>
          ))}
        </Pane>

        {/* Every disposition this fault has been given, in order. The body of a
            squawk accumulates maintenance notes; this says who changed its
            status and when, which the body cannot. */}
        <Pane title="What has been done">
          <History
            compact
            entity="squawk"
            entityId={squawk.id}
            emptyNote="Nothing recorded since it was filed."
          />
        </Pane>
      </Panes>
    </Drawer>
  );
}
